import { useLayoutEffect, useMemo, useState } from 'react'
import { findSpellWordElement } from '../handlers/find-spell-word-element'
import { findCanvasScroller, revealSpellWord } from '../handlers/reveal-spell-word'
import type { SpellWordTarget } from '../types/spell-word-target'

/** The shape Radix's `PopoverAnchor` takes for a `virtualRef`. */
interface WordAnchorRef {
  current: { getBoundingClientRect: () => DOMRect; contextElement: Element }
}

/**
 * How many animation frames to wait for the word's marker to be drawn. A word in hidden text only
 * gets one once the peek has mounted and found where the hidden text ends, which takes a few
 * frames; a visible word's marker is already there. Past this the word is treated as not drawn and
 * the popover stays on its row.
 */
const MAX_FRAMES = 30

interface SpellWordAnchor {
  /** What to anchor the popover to, or `null` to leave it on its row. */
  anchor: WordAnchorRef | null
  /**
   * True from the moment a word is open until its marker has been found and the canvas has moved to
   * it. The popover waits, out of sight, so it appears where the word is and never on the way there.
   */
  isSearching: boolean
  /** The canvas the word is on, so the popover can stay inside it instead of covering the panels. */
  boundary: Element | null
}

/**
 * Finds the misspelled word the Spell Check popover is on, so the popover can open directly at the
 * word on the canvas, as in the design demo, and not beside its panel row, and brings the word into
 * view.
 *
 * Looking is syncing with the DOM the canvas drew, so it is an effect. A word whose marker is already
 * drawn is found before the browser paints; one that is not yet (hidden text, once its peek has
 * mounted) is looked for from animation frames. When the marker is found the canvas moves to
 * the word at once, and only then does the popover show, so it appears in its final place with
 * nothing sliding or jumping on the way. The anchor reads the marker's position each time the popover
 * asks, so it follows the canvas if the user scrolls it.
 */
export function useSpellWordAnchor(
  target: SpellWordTarget | null,
  /** Names the popover the word belongs to; words of one session are stepped through, not reopened. */
  session: string | null = null
): SpellWordAnchor {
  const [found, setFound] = useState<{
    key: string
    session: string | null
    word: SpellWordTarget
    element: HTMLElement | null
  } | null>(null)
  const key = target ? `${target.shapeId}|${target.start}-${target.end}` : null
  const shapeId = target?.shapeId
  const start = target?.start
  const end = target?.end

  // A layout effect, so a word that is already drawn is found before the browser paints.
  useLayoutEffect(() => {
    if (key === null || shapeId === undefined || start === undefined || end === undefined) {
      // Nothing is open any more: forget the last word so the next popover starts afresh.
      setFound(null)

      return
    }

    let frames = 0
    let handle = 0

    const settle = (element: HTMLElement | null) => {
      // Moved before the popover is shown, so its first position is the final one.
      if (element) revealSpellWord(element)

      setFound({ key, session, word: { shapeId, start, end }, element })
    }

    // A word that is already drawn is anchored before the browser paints, so stepping from one
    // instance to the next moves the popover straight to it, with no frame in between where it is
    // hidden or parked on its row.
    const drawn = findSpellWordElement(shapeId, start, end)

    if (drawn) {
      settle(drawn)

      return
    }

    const look = () => {
      const element = findSpellWordElement(shapeId, start, end)

      if (element || ++frames >= MAX_FRAMES) {
        settle(element)

        return
      }

      handle = requestAnimationFrame(look)
    }

    handle = requestAnimationFrame(look)

    return () => cancelAnimationFrame(handle)
  }, [key, session, shapeId, start, end])

  const isCurrent = found !== null && found.key === key
  // Stepping within one popover keeps it where it is until the next word is found, so it moves in
  // one jump, as in the demo, instead of vanishing and reappearing. Another popover never inherits it.
  const isStepping = !isCurrent && found !== null && session !== null && found.session === session
  const shown = isCurrent || isStepping ? found : null
  const element = target && shown ? shown.element : null

  const anchor = useMemo<WordAnchorRef | null>(() => {
    if (!element || !shown) return null

    const { word } = shown
    let lastRect: DOMRect | null = null

    return {
      current: {
        // The marker can be redrawn between asks, so look it up again and keep this one as a fallback.
        // A marker that is gone for good (the word moved on) holds the place it was last seen.
        getBoundingClientRect: () => {
          const live = findSpellWordElement(word.shapeId, word.start, word.end) ?? element

          if (live.isConnected || lastRect === null) lastRect = live.getBoundingClientRect()

          return lastRect
        },
        // Lets the popover follow the scroll containers around the marker.
        contextElement: element,
      },
    }
  }, [element, shown])

  const boundary = useMemo(() => (element ? findCanvasScroller(element) : null), [element])

  return { anchor, isSearching: key !== null && !isCurrent && !isStepping, boundary }
}
